import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Spinner } from "@/components/Bits";
import AppLayout from "@/components/AppLayout";
import { Login, ForgotPassword, ResetPassword } from "@/pages/AuthPages";
import Register from "@/pages/Register";
import AcceptInvite from "@/pages/AcceptInvite";
import Legal from "@/pages/Legal";
import NotFound from "@/pages/NotFound";
import Dashboard from "@/pages/Dashboard";
import Matches from "@/pages/Matches";
import MatchDetail from "@/pages/MatchDetail";
import MyPredictions from "@/pages/MyPredictions";
import Leagues from "@/pages/Leagues";
import CreateLeague from "@/pages/CreateLeague";
import LeagueDetail from "@/pages/LeagueDetail";
import Ranking from "@/pages/Ranking";
import Profile from "@/pages/Profile";
import AccountSettings from "@/pages/AccountSettings";
import Notifications from "@/pages/Notifications";
import QuickPicks from "@/pages/QuickPicks";
import Setup from "@/pages/Setup";
import CorporateJoin from "@/pages/CorporateJoin";
import AdminSync from "@/pages/AdminSync";
import SuperAdmin from "@/pages/admin/SuperAdmin";
import Unsubscribe from "@/pages/Unsubscribe";
import BrandedLeagues from "@/pages/BrandedLeagues";
import Offers from "@/pages/Offers";
import PartnerPage from "@/pages/PartnerPage";
import PartnerPortal, { PartnerInvite } from "@/pages/PartnerPortal";
import PreviewShare from "@/pages/PreviewShare";
import PublicShell from "@/components/public/PublicShell";
import Home from "@/pages/public/Home";
import Universe from "@/pages/public/Universe";
import ExperiencesPage from "@/pages/public/ExperiencesPage";
import ExperienceDetail from "@/pages/public/ExperienceDetail";
import FeedPage from "@/pages/public/FeedPage";
import OrgPublic from "@/pages/public/OrgPublic";

function Protected({ children, admin }) {
  const { user } = useAuth();
  const loc = useLocation();
  if (user === null) return <div className="flex min-h-screen justify-center bg-[#0B1020]"><Spinner /></div>;
  if (!user) return <Navigate to={`/connexion?suite=${encodeURIComponent(loc.pathname + loc.search)}`} replace />;
  if (admin && user.role !== "superadmin") return <Navigate to="/tableau-de-bord" replace />;
  return children;
}

function PublicOnly({ children }) {
  const { user } = useAuth();
  if (user) return <Navigate to="/tableau-de-bord" replace />;
  return children;
}

function ScrollToTop() {
  const { pathname, hash } = useLocation();
  useEffect(() => { if (!hash) window.scrollTo(0, 0); }, [pathname, hash]);
  return null;
}

export default function App() {
  return (
    <BrowserRouter>
      <ScrollToTop />
      <AuthProvider>
        <Routes>
          <Route element={<PublicShell />}>
            <Route path="/" element={<Home />} />
            <Route path="/play" element={<Universe page="play" />} />
            <Route path="/communities" element={<Universe page="communities" />} />
            <Route path="/business" element={<Universe page="business" />} />
            <Route path="/partners" element={<Universe page="partners" />} />
            <Route path="/experiences" element={<ExperiencesPage />} />
            <Route path="/experience/:slug" element={<ExperienceDetail />} />
            <Route path="/feed" element={<FeedPage />} />
            <Route path="/community/:slug" element={<OrgPublic kind="COMMUNITY" />} />
            <Route path="/partner/:slug" element={<OrgPublic kind="PARTNER" />} />
          </Route>
          <Route path="/connexion" element={<PublicOnly><Login /></PublicOnly>} />
          <Route path="/inscription" element={<Register />} />
          <Route path="/invitation/:token" element={<AcceptInvite />} />
          <Route path="/e/:slug" element={<CorporateJoin />} />
          <Route path="/ligue-a-ton-image" element={<BrandedLeagues />} />
          <Route path="/partenaire/invitation/:token" element={<PartnerInvite />} />
          <Route path="/apercu/:token" element={<PreviewShare />} />
          <Route path="/partenaire" element={<Protected><PartnerPortal /></Protected>} />
          <Route path="/mot-de-passe-oublie" element={<ForgotPassword />} />
          <Route path="/reinitialiser" element={<ResetPassword />} />
          <Route path="/confidentialite" element={<Legal kind="privacy" />} />
          <Route path="/conditions" element={<Legal kind="terms" />} />
          <Route path="/desabonnement" element={<Unsubscribe />} />
          <Route element={<Protected><AppLayout /></Protected>}>
            <Route path="/tableau-de-bord" element={<Dashboard />} />
            <Route path="/matchs" element={<Matches />} />
            <Route path="/pronos-rapides" element={<QuickPicks />} />
            <Route path="/configurer" element={<Setup />} />
            <Route path="/matchs/:id" element={<MatchDetail />} />
            <Route path="/mes-predictions" element={<MyPredictions />} />
            <Route path="/offres" element={<Offers />} />
            <Route path="/offres/partenaire/:id" element={<PartnerPage />} />
            <Route path="/ligues" element={<Leagues />} />
            <Route path="/ligues/nouvelle" element={<CreateLeague />} />
            <Route path="/ligues/:id" element={<LeagueDetail />} />
            <Route path="/ligues/:id/:tab" element={<LeagueDetail />} />
            <Route path="/classement" element={<Ranking />} />
            <Route path="/profil" element={<Profile />} />
            <Route path="/parametres" element={<AccountSettings />} />
            <Route path="/notifications" element={<Notifications />} />
            <Route path="/admin/synchronisation" element={<Protected admin><AdminSync /></Protected>} />
            <Route path="/admin" element={<Protected admin><SuperAdmin /></Protected>} />
            <Route path="/admin/:tab" element={<Protected admin><SuperAdmin /></Protected>} />
            <Route path="*" element={<NotFound inApp />} />
          </Route>
          <Route path="*" element={<NotFound />} />
        </Routes>
        <Toaster position="top-center" theme="dark" richColors offset="calc(env(safe-area-inset-top) + 16px)" />
      </AuthProvider>
    </BrowserRouter>
  );
}
